import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { Check, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Backward-compat: flat ya da nested content alani destegi.
 * @param {Object} lesson
 * @returns {string}
 */
function getContent(lesson) {
  return lesson.content || lesson.text?.content || '';
}

// Yuvarlama payi — scroll-edilebilirlik olcumunde tek yerden kullanilir (mount
// effect'i ve manuel-tamamlama catch fallback'i AYNI esigi paylasmali, aksi halde
// biri "scroll edilemez" derken digeri "edilebilir" diyebilir).
const SCROLL_SLACK_PX = 4;

/**
 * isContentScrollable — verilen DOM elemani gercekten scroll edilebilir mi?
 * (icerigi clientHeight'i asiyor mu, yuvarlama payi ile birlikte)
 * @param {HTMLElement|null} el
 * @returns {boolean}
 */
function isContentScrollable(el) {
  if (!el) return false;
  return el.scrollHeight > el.clientHeight + SCROLL_SLACK_PX;
}

/**
 * TextLesson — metin icerik + scroll-to-bottom + "Okudum" akisi.
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function TextLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const lessonId = lesson.lesson_id;
  const [textScrolledToBottom, setTextScrolledToBottom] = useState(false);
  const [textRead, setTextRead] = useState(false);
  const [completing, setCompleting] = useState(false);
  const textContentRef = useRef(null);
  const isCompleted = completedLessonIds.includes(lessonId);

  const content = getContent(lesson);

  const handleTextScroll = () => {
    const el = textContentRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (atBottom && !textScrolledToBottom) setTextScrolledToBottom(true);
  };

  // Ders degisince onceki dersten kalan "okundu" durumu yeni derse sizmasin.
  useEffect(() => {
    setTextRead(false);
    setTextScrolledToBottom(false);
  }, [lessonId]);

  // Bug fix: icerik kisa oldugunda (60vh'yi doldurmuyor) scrollbar hic olusmuyor,
  // bu yuzden onScroll asla tetiklenmiyor ve "Okudum" butonu hic gorunmuyordu.
  // Icerik scroll edilebilir DEGILSE (veya bossa) otomatik "sona ulasildi" kabul edilir.
  useEffect(() => {
    const el = textContentRef.current;
    if (!el) return;

    const checkScrollable = () => {
      if (!isContentScrollable(el)) setTextScrolledToBottom(true);
    };

    checkScrollable();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(checkScrollable);
      ro.observe(el);
    }
    window.addEventListener('resize', checkScrollable);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', checkScrollable);
    };
  }, [content, lessonId]);

  const handleManualComplete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, text_read: textRead },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      // Guvenlik agi: backend text_not_read ile reddederse VE icerik gercekten
      // scroll edilemiyorsa (kisa metin, mount effect'in kacirdigi bir durum)
      // butonu gorunur kil. Icerik scroll edilebiliyorsa DOKUNMA — kullanici
      // gercekten sonuna kadar okumadan tamamlamaya calisiyor demektir, anti-skip
      // korumasi burada bypass EDILMEMELI (bkz. platform geneli scroll zorunlulugu).
      const errorCode = err?.response?.data?.detail?.error_code;
      if (errorCode === 'text_not_read' && !isContentScrollable(textContentRef.current)) {
        setTextScrolledToBottom(true);
      }
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <>
      <section
        className="card-clay p-6 mb-6"
        data-testid="lesson-text-content"
        aria-label={lang === 'tr' ? 'Ders Icerigi' : 'Lesson Content'}
      >
        <h3 className="font-bold text-sm mb-3">
          {lang === 'tr' ? 'Ders Icerigi' : 'Lesson Content'}
        </h3>

        <div
          ref={textContentRef}
          onScroll={handleTextScroll}
          className="text-sm text-[var(--text-muted)] whitespace-pre-line max-h-[60vh] overflow-y-auto pr-2"
          data-testid="text-scroll-area"
          tabIndex={0}
          aria-label={lang === 'tr' ? 'Ders metni' : 'Lesson text'}
        >
          {content || (lang === 'tr' ? 'İçerik bulunamadı.' : 'No content available.')}
        </div>

        {textScrolledToBottom && !textRead && (
          <button
            onClick={() => setTextRead(true)}
            className="mt-4 px-5 py-2 bg-blue-500 text-white rounded-xl font-bold text-sm hover:bg-blue-600"
            data-testid="mark-text-read-btn"
          >
            <Check size={16} className="inline mr-1" />
            {lang === 'tr' ? 'Okudum' : 'I read it'}
          </button>
        )}
        {textRead && (
          <div className="mt-3 text-xs font-semibold text-green-600" data-testid="text-read-status">
            <CheckCircle size={12} className="inline mr-1" />
            {lang === 'tr' ? 'Okundu olarak isaretlendi' : 'Marked as read'}
          </div>
        )}
      </section>

      {/* Complete bar */}
      {!isCompleted && (
        <div className="card-clay p-4 mb-6 flex items-center justify-between gap-4" data-testid="complete-lesson-bar">
          <div className="text-sm font-semibold text-[var(--text-muted)]">
            {lang === 'tr' ? 'Dersi bitirdin mi?' : 'Finished this lesson?'}
          </div>
          <button
            onClick={handleManualComplete}
            disabled={completing}
            className="btn-success px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="complete-lesson-btn"
          >
            <CheckCircle size={16} />
            {completing ? '...' : (lang === 'tr' ? 'Dersi Tamamla' : 'Complete Lesson')}
          </button>
        </div>
      )}
      {isCompleted && (
        <div className="card-clay p-4 mb-6 text-center bg-green-50 border-green-200" data-testid="lesson-already-completed">
          <CheckCircle size={20} className="inline text-green-600 mr-2" />
          <span className="font-bold text-sm text-green-700">
            {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
          </span>
        </div>
      )}
    </>
  );
}
